/**
 * Client-side feature toggle helper used as an integration point for
 * third-party A/B testing tools.
 *
 * Exposed globally as `window.wovarFeatureToggle(name, enabled, options?)`.
 * The toggle is broadcast via a CustomEvent so React components subscribed
 * through `useFeatureToggle` re-render.
 *
 * `options.paths` restricts the toggle to those pages (WOV-2195); omitting it
 * applies the toggle everywhere, which is the original behaviour. The scope
 * travels with the value because in-site navigation never reloads the page,
 * so the testing tool cannot re-apply the toggle per page.
 *
 * When `options.persistent` is true the value is mirrored to localStorage
 * and survives reloads; otherwise the toggle lives only
 * for the current page session.
 *
 * The toggle is purely client-side: server rendering uses the component's
 * `defaultEnabled` value and any in-memory / persisted override is applied
 * after hydration.
 */

declare global {
  interface Window {
    wovarFeatureToggle?: (
      name: string,
      enabled: boolean,
      options?: FeatureToggleOptions
    ) => void;
  }
}

const STORAGE_PREFIX = "wv-ft-";

const RETIRED_FEATURE_TOGGLES = new Set(["product-list-show-total-count"]);

const storageKey = (name: string) => `${STORAGE_PREFIX}${name}`;

/**
 * In-memory cache of the latest set state per feature name.
 *
 * Lets a component that mounts *after* a toggle was set still observe the
 * current value (the dispatched CustomEvent only reaches listeners that
 * were already attached).
 */
const memoryCache = new Map<string, FeatureToggleState>();

const purgeFeatureToggle = (name: string): void => {
  memoryCache.delete(name);
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.removeItem(storageKey(name));
  } catch {}
};

const purgeRetiredFeatureToggles = (): void => {
  for (const name of RETIRED_FEATURE_TOGGLES) {
    purgeFeatureToggle(name);
  }
};

export type FeatureToggleOptions = {
  /** Mirror the value to localStorage so it survives reloads. */
  persistent?: boolean;
  /**
   * Pages the toggle applies to, e.g. `["/bevestigingsmaterialen/"]`.
   * Omitted means every page. Query strings and casing are ignored when matching.
   */
  paths?: string[];
  /**
   * Also apply to every category below the listed ones. Category URLs are flat
   * (`/pluggen/` is a child of `/bevestigingsmaterialen/`), so the match uses the
   * page's breadcrumb ancestors rather than the URL.
   */
  includeChildren?: boolean;
};

export type FeatureToggleState = {
  enabled: boolean;
  paths?: string[];
  includeChildren?: boolean;
};

export type FeatureToggleEventDetail = FeatureToggleState & {
  name: string;
};

export const FEATURE_TOGGLE_EVENT = "wv:feature-toggle-change";

/** WOV-2195: numbered pagination instead of infinite scroll on category pages. */
export const CATEGORY_PAGINATION_TOGGLE = "category-pagination";

/** Lowercase, slash-wrapped path so `/Tuin`, `/tuin` and `/tuin/` all match. */
const normalizePath = (path: string): string => {
  const [withoutQuery] = path.split(/[?#]/);
  const trimmed = withoutQuery.trim().toLowerCase();
  const leading = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return leading.endsWith("/") ? leading : `${leading}/`;
};

/**
 * No scope means the toggle applies everywhere.
 *
 * `ancestorPaths` are the current page's breadcrumb paths; they only matter when
 * the scope was set with `includeChildren`.
 */
export const isPathInScope = (
  paths: string[] | undefined,
  pathname: string,
  options: { includeChildren?: boolean; ancestorPaths?: string[] } = {}
): boolean => {
  if (paths === undefined) {
    return true;
  }

  const scope = paths.map(normalizePath);
  if (scope.includes(normalizePath(pathname))) {
    return true;
  }

  if (!options.includeChildren) {
    return false;
  }

  return (options.ancestorPaths ?? []).some((ancestor) =>
    scope.includes(normalizePath(ancestor))
  );
};

const parseStored = (raw: string): FeatureToggleState | undefined => {
  // Legacy entries hold "1"/"0"; scoped ones hold JSON.
  if (raw === "1" || raw === "0") {
    return { enabled: raw === "1" };
  }

  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed?.enabled !== "boolean") {
      return undefined;
    }
    return {
      enabled: parsed.enabled,
      paths: Array.isArray(parsed.paths) ? parsed.paths : undefined,
      includeChildren: parsed.includeChildren === true ? true : undefined,
    };
  } catch {
    return undefined;
  }
};

/**
 * Reads the current state for `name`. Memory wins over persisted state
 * a non-persistent call made earlier this session takes precedence over
 * any stale localStorage entry. Returns `undefined` when nothing is set.
 */
export const getFeatureToggleState = (
  name: string
): FeatureToggleState | undefined => {
  if (RETIRED_FEATURE_TOGGLES.has(name)) {
    purgeFeatureToggle(name);
    return undefined;
  }

  const cached = memoryCache.get(name);
  if (cached !== undefined) {
    return cached;
  }

  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    const raw = window.localStorage.getItem(storageKey(name));
    if (raw === null) {
      return undefined;
    }

    const state = parseStored(raw);
    if (state === undefined) {
      return undefined;
    }

    // Promote into memory so subsequent reads don't hit localStorage again.
    memoryCache.set(name, state);

    return state;
  } catch {}

  // Private mode / disabled storage treat as unset.
  return undefined;
};

/** Value only, ignoring any page scope. */
export const getFeatureToggle = (name: string): boolean | undefined =>
  getFeatureToggleState(name)?.enabled;

const sameState = (a: FeatureToggleState, b: FeatureToggleState): boolean => {
  if (a.enabled !== b.enabled || a.includeChildren !== b.includeChildren) {
    return false;
  }
  if (a.paths === undefined || b.paths === undefined) {
    return a.paths === b.paths;
  }
  return (
    a.paths.length === b.paths.length &&
    a.paths.every((path, index) => path === b.paths?.[index])
  );
};

/**
 * Sets the toggle.
 *
 * The call updates in-memory state and dispatches a CustomEvent
 * so subscribed hooks re-render. When `options.persistent` is true
 * the value is also written to localStorage; otherwise any prior
 * persisted value for this name is cleared so the latest call is always
 * authoritative.
 */
export const setFeatureToggle = (
  name: string,
  enabled: boolean,
  options: FeatureToggleOptions = {}
): void => {
  if (typeof window === "undefined") {
    return;
  }

  if (RETIRED_FEATURE_TOGGLES.has(name)) {
    purgeFeatureToggle(name);
    return;
  }

  const state: FeatureToggleState = {
    enabled,
    paths: options?.paths,
    includeChildren: options?.includeChildren === true ? true : undefined,
  };
  const stored = options?.persistent ? JSON.stringify(state) : null;

  // No-op when nothing observable would change.
  // Avoids wasted dispatches when an A/B tool re-applies the same bucket on every navigation.
  const current = memoryCache.get(name);
  if (current !== undefined && sameState(current, state)) {
    try {
      if (window.localStorage.getItem(storageKey(name)) === stored) {
        return;
      }
    } catch {
      // Storage unreadable fall through and do the work anyway.
    }
  }

  memoryCache.set(name, state);

  try {
    if (stored !== null) {
      window.localStorage.setItem(storageKey(name), stored);
    } else {
      window.localStorage.removeItem(storageKey(name));
    }
  } catch {
    // Storage unavailable in-memory state still applies for this session.
  }

  const detail: FeatureToggleEventDetail = { name, ...state };
  window.dispatchEvent(new CustomEvent(FEATURE_TOGGLE_EVENT, { detail }));
};

/** Every entry must be a non-empty string, or the whole scope is untrustworthy. */
const isValidPaths = (paths: unknown): paths is string[] =>
  Array.isArray(paths) &&
  paths.length > 0 &&
  paths.every((path) => typeof path === "string" && path.trim() !== "");

/**
 * Installs `window.wovarFeatureToggle`.
 *
 * Idempotent safe to call multiple times.
 */
export const installGlobalFeatureToggle = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  purgeRetiredFeatureToggles();

  if (typeof window.wovarFeatureToggle === "function") {
    return;
  }

  window.wovarFeatureToggle = (name, enabled, options) => {
    if (typeof name !== "string" || typeof enabled !== "boolean" || !name.trim()) {
      return;
    }

    // Rather than silently widening the scope to every page, reject the call so
    // a malformed list shows up as "the variant never renders".
    if (options?.paths !== undefined && !isValidPaths(options.paths)) {
      console.warn(
        `[wovarFeatureToggle] ignoring "${name}": options.paths must be a non-empty array of strings`
      );
      return;
    }

    if (options?.includeChildren !== undefined && typeof options.includeChildren !== "boolean") {
      console.warn(
        `[wovarFeatureToggle] ignoring "${name}": options.includeChildren must be a boolean`
      );
      return;
    }

    setFeatureToggle(name, enabled, options);
  };
};
